/**
 * V3MessageList - Renders chat messages using MarkdownRenderer for assistant content.
 *
 * Consecutive assistant messages are merged into a single bubble to avoid the
 * "split messages" look caused by the backend saving one message per agent iteration.
 */

import React, { useMemo, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { MarkdownRenderer } from './MarkdownRenderer';
import { MessageActionButtons } from '../shared/MessageActionButtons';
import { SaveToResearchDialog } from '../shared/SaveToResearchDialog';
import { formatAdaptiveTimestamp } from '../../utils/formatTimestamp';
import { stripRedactionPrefix } from '../../utils/stripRedactionPrefix';
import { hasMeaningfulChatContent } from '../../utils/hasMeaningfulChatContent';
import type { ChatMessage } from '@/types/chat';

interface V3MessageListProps {
  messages: ChatMessage[];
  caseId?: string;
  /** Filenames currently being uploaded — shows a spinner on matching file chips */
  uploadingFilenames?: string[];
}

/** A visual group: either a single user message or N merged assistant messages */
interface MessageGroup {
  id: string;
  role: 'user' | 'assistant';
  /** For user messages: single content string. For assistant: joined content. */
  content: string;
  messages: ChatMessage[];
}

/**
 * Merge consecutive assistant messages into a single group so they render
 * as one bubble instead of N separate ones.
 */
function groupMessages(messages: ChatMessage[]): MessageGroup[] {
  const groups: MessageGroup[] = [];

  for (const msg of messages) {
    const last = groups[groups.length - 1];

    if (msg.role === 'assistant' && last?.role === 'assistant') {
      last.content += '\n\n' + msg.content;
      last.messages.push(msg);
    } else {
      groups.push({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        messages: [msg],
      });
    }
  }

  return groups;
}

export const V3MessageList: React.FC<V3MessageListProps> = ({ messages, caseId, uploadingFilenames = [] }) => {
  const groups = useMemo(() => groupMessages(messages), [messages]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [messageToSave, setMessageToSave] = useState<ChatMessage | null>(null);

  const handleSaveToResearch = (group: MessageGroup) => {
    // Build a synthetic ChatMessage from the merged group
    const syntheticMessage: ChatMessage = {
      id: group.id,
      conversationId: group.messages[0]?.conversationId ?? '',
      role: 'assistant',
      content: group.content,
      createdAt: group.messages[0]?.createdAt ?? new Date(),
    };
    setMessageToSave(syntheticMessage);
    setSaveDialogOpen(true);
  };

  if (groups.length === 0) return null;

  return (
    <>
      <div className="space-y-2">
        {groups.map((group) => (
          <V3MessageBubble
            key={group.id}
            group={group}
            uploadingFilenames={uploadingFilenames}
            onSaveToResearch={() => handleSaveToResearch(group)}
          />
        ))}
      </div>

      <SaveToResearchDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        message={messageToSave}
        caseId={caseId}
        preselectedCaseId={caseId}
      />
    </>
  );
};

function V3MessageBubble({ group, onSaveToResearch, uploadingFilenames = [] }: { group: MessageGroup; onSaveToResearch: () => void; uploadingFilenames?: string[] }) {
  const isUser = group.role === 'user';

  // User messages: right-aligned rounded "bubble" (no avatar, no name label) —
  // mirrors the edition-v3 / claude.ai / chatgpt chat conventions.
  if (isUser) {
    const attachedFilenames = (group.messages[0]?.metadata?.attachedFilenames as string[] | undefined) ?? [];
    const sentAt = group.messages[0]?.createdAt;
    const visibleUserContent = stripRedactionPrefix(group.content);
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-2">
        <div className="flex justify-end">
          <div className="group flex max-w-[85%] flex-col items-end">
            {/* Attached file chips */}
            {attachedFilenames.length > 0 && (
              <div className="flex flex-wrap justify-end gap-1.5 mb-1.5">
                {attachedFilenames.map((name: string) => {
                  const isUploading = uploadingFilenames.includes(name);
                  return (
                    <div key={name} className="flex items-center gap-1.5 bg-grey-100 border border-grey-200 rounded-lg px-2 py-1">
                      {isUploading ? (
                        <Loader2 className="w-3 h-3 text-grey-400 flex-shrink-0 animate-spin" />
                      ) : (
                        <FileText className="w-3 h-3 text-grey-400 flex-shrink-0" />
                      )}
                      <span className="text-xs text-grey-600 max-w-[160px] truncate">{name}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {hasMeaningfulChatContent(visibleUserContent) && (
              <div className="rounded-2xl bg-grey-100 px-4 py-2.5 text-[13px] text-grey-800 leading-relaxed whitespace-pre-wrap break-words">
                {visibleUserContent}
              </div>
            )}
            {/* Sent-at time (HH:mm), revealed on hover — muted one notch lighter than the bubble text */}
            {sentAt && (
              <span className="mt-1 text-[11px] text-grey-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                {formatAdaptiveTimestamp(sentAt)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Assistant messages: plain full-width text, no avatar, no name label.
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-2">
      <div className="group min-w-0">
        <MarkdownRenderer
          content={group.content}
          className="max-w-none text-[13px] text-grey-800 leading-relaxed text-justify"
        />
        <MessageActionButtons
          messageContent={group.content}
          messageId={group.id}
          onSaveToResearch={onSaveToResearch}
          className="mt-1.5"
        />
      </div>
    </div>
  );
}
